import axios, { type AxiosResponse } from "axios";

/**
 * Downloads a file from a given URL.
 *
 * @param url - The URL of the file to be downloaded.
 * @param filename - Optional. The name to be given to the downloaded file.
 * If no filename is given, the function will use the name from the response.
 */
export async function DownloadFile(url: string, filename?: string, signal?: AbortSignal): Promise<void> {
	const response = await axios.get(url, {
		responseType: "blob",
		signal,
	});

	if (signal?.aborted) {
		return;
	}

	const fileNameFromResponse = getFileNameFromResponse(response) ?? "";

	showFile(response.data, filename
		? filename
		: fileNameFromResponse,
	);
}

/**
 See https://stackoverflow.com/a/67994693
*/
function getFileNameFromResponse(response: AxiosResponse): string | null {
	const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i;
	const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;
	const disposition = response.headers["content-disposition"];

	if (!disposition) {
		return null;
	}

	let fileName: string | null = null;
	if (utf8FilenameRegex.test(disposition)) {
		fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)?.[1] ?? "");
	}
	else {
		// prevent ReDos attacks by anchoring the ascii regex to string start and
		//  slicing off everything before 'filename='
		const filenameStart = disposition.toLowerCase().indexOf("filename=");
		if (filenameStart >= 0) {
			const partialDisposition = disposition.slice(filenameStart);
			const matches = asciiFilenameRegex.exec(partialDisposition);
			if (matches != null && matches[2]) {
				fileName = matches[2];
			}
		}
	}
	return fileName;
}

function showFile(blob: Blob, fileName: string) {
	// It is necessary to create a new blob object with mime-type explicitly set
	// otherwise only Chrome works like it should
	const newBlob = new Blob([blob], { type: blob.type });

	// Create a link pointing to the ObjectURL containing the blob.
	const data = window.URL.createObjectURL(newBlob);
	const link = document.createElement("a");
	link.href = data;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(data);
}
